import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { DESCRIPTION, NAME } from "../consts";
import { routes } from "@/routes/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { formValues, schema } from "./validations";
import { useAddInventorySetupMutation } from "@/services/masterData";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import useUpdateInventorySetupsMutation from "@/services/masterData/inventorysetup/useUpdateInventorySetupMutation";
import useGetInventorySetupDetailQuery from "@/services/masterData/inventorysetup/useInventorySetupDetailMutation";
import { useEffect } from "react";

const useAddEdit = () => {
  const { action, id } = useParams();
  const navigate = useNavigate();
  const isView = action == routes.view;
  const isEdit = action == routes.edit;
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [NAME]: "",
      [DESCRIPTION]: "",
    },
  });

  const requiredFields = getRequiredFields(schema);

   const { data: detailRes, isPending: isDetailLoading } =
    useGetInventorySetupDetailQuery(isEdit || isView ? String(id) : undefined);

  const { mutateAsync: updateInventorySetup, isPending: isUpdating } =
    useUpdateInventorySetupsMutation({
      onSuccess: () => {
        toast.success("Inventory Setup updated successfully");
        onBackClick();
      },
      onError: (error: any) => {
        toast.error(
          error.response.data?.issues?.message ||
            error.response.data.message ||
            "Failed to update inventory setup"
        );
      },
    });

  const { mutateAsync: addInventory, isPending } = useAddInventorySetupMutation(
    {
      onSuccess: () => {
        toast.success("Inventory created successfully");
        onBackClick();
      },
      onError: (error: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error.response.data.message
            : "Failed to create inventory"
        );
      },
    }
  );

    
    useEffect(() => {
      if ((isEdit || isView) && detailRes?.data) {
        const d = detailRes.data;
        form.reset({
          [NAME]: String(d?.name ?? ""),
          [DESCRIPTION]: d?.description ? String(d?.description) : "",
        });

      }
    }, [detailRes, isEdit, isView, form]);

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.masterData}`);
  };

  const onSubmit = async (data: formValues) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);
    if (isEdit && id) {
      await updateInventorySetup({ id, data: _data });
      queryClient.invalidateQueries({ queryKey: ["inventorySetup"] });
      return;
    }
    await addInventory({ data: _data });
    queryClient.invalidateQueries({ queryKey: ["inventorySetup"] });
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel: onBackClick,
    isMobile,
    action,
    isEdit,
    isLoading: isPending,
  };
};

export default useAddEdit;
